import { closest } from 'components/Form/Generic';
import { capitalize, toTitleCase } from 'helpers/string';
import { ErrorListMessages, ErrorListMessageContext } from './types';

/**
 * Find the closest section title to the error.
 */
const getSectionTitle = (el: Element): string | null => {
  const content = closest(el, '.section-content') as HTMLDivElement | null;

  if (!content) { return null; }

  const header = content.querySelector<HTMLHeadingElement>('h1, h2, h3, h4, h5, h6');

  return header?.textContent?.trim() ?? null;
};

/**
 * Find the accordion summary.
 */
const getAccordionSummary = (el: Element): string | null => {
  const accordionItem = closest(el, '.item');
  if (!accordionItem) { return null; }

  const summary = accordionItem.querySelector<HTMLDivElement>('.summary-item-content');
  if (!summary) { return null; }

  if (summary.classList.contains('has-content')) {
    return summary.innerText.replace(':', ': ').trim();
  }

  const summaryIndex = summary.querySelector<HTMLDivElement>('.index');

  return summaryIndex?.innerText?.replace(':', '').trim() ?? null;
};

/**
 * Find the field title.
 */
const getFieldTitle = (id: string | null): string | null => {
  const field = document.querySelector(`.field[data-uuid="${id}"]`);
  if (!field) { return null; }

  const title = field.querySelector<HTMLSpanElement>('.title');

  return title?.textContent?.trim() ?? null;
};

/**
 * Find the field unique identifier.
 */
const getFieldId = (el: Element): string | null => {
  const field = closest(el, '.field') as HTMLDivElement | null;

  return field?.dataset?.uuid ?? null;
};

/**
 * Determines if the element is within an accordion element.
 */
const inAccordion = (el: Element): boolean => closest(el, '.accordion') !== null;

/**
 * Group an array by a specific property and return as a hash.
 */
const groupBy = (arr: ErrorListMessageContext[]): ErrorListMessages => {
  const context: ErrorListMessages = {
    messages: {},
    count: arr.length,
  };

  if (!arr || arr.length === 0) { return context; }

  for (const item of arr) {
    const key = item.title;
    if (key === null) { continue; }

    if (!context.messages[key]) {
      context.messages[key] = [];
    }
    context.messages[key].push(item);
  }

  return context;
};

/**
 * Find all error messages in the document.
 */
export const getErrorMessages = (): ErrorListMessages => {
  const elements = document.querySelectorAll(
    ':not(.error-list) .field .messages .message.error, :not(.error-list) .field .messages .message.error-message, .field .gap .usa-alert.usa-alert-error'
  );
  const messages = [];

  for (const el of elements) {
    const id = getFieldId(el);
    const message = getFieldTitle(id);
    const title = inAccordion(el) ? getAccordionSummary(el) : getSectionTitle(el);
    messages.push({ id, title, message });
  }

  return groupBy(messages.filter((m) => m.title && m.message));
};

export const cleanQuestion = (question: string | undefined) => {
  if (question === undefined) { return undefined; }

  const newQuestion = question === 'ssn' ? 'Social Security Number' : question;

  return capitalize(newQuestion);
};

export const cleanField = (field: string | undefined) => {
  if (field === undefined) { return undefined; }
  const ignoredFields = ['value', 'checked'];

  if (ignoredFields.includes(field)) { return undefined; }

  return capitalize(field);
};

export const getQuestion = (attribute: string) => {
  const splits = attribute.split('.');
  const question = cleanQuestion(
    splits
      .filter(
        (s, i, array) => (i === array.length - 2 || (array.length === 1 && i === 0)) && !s.includes('[')
      )
      .join('.')
  )?.replace('Item', '');
  const field = cleanField(splits.find((s, i, array) => i === array.length - 1));

  return toTitleCase(
    `${question}${question && field && field !== question ? ': ' : ''}${field && field !== question ? field : ''}`
  );
};

type ItemType = {
  Item: any
}
export const handleListHeaderTemplate = (index: number, item?: ItemType, listHeader?: string) => {
  // Fallback when undefined
  if (!item || !listHeader) { return `Item ${index}`; }
  const uselessCharacters = /[., ]/g;
  const bracketRegex = /({[A-Za-z.0-9]*})/g;
  const emptyHeader = listHeader.replace(uselessCharacters, '').replace(bracketRegex, '');

  // Checks for letters and periods. When periods exist, it does child mapping.
  const matches = listHeader.match(bracketRegex);
  let header = listHeader;

  if (matches) {
    matches.forEach((argMatch) => {
      const match = argMatch.replace(/[{}]/g, '');
      const splits = match.split('.');
      let subItem = item.Item;

      splits.forEach((i) => {
        subItem = subItem?.[i];
      });

      const value = subItem;

      header = header.replace(argMatch, value);
    });

    header = header.replace(/undefined/g, '').trim();

    // Whenever there isn't enough data, fallback.
    const testHeader = header.replace(uselessCharacters, '');
    if (testHeader.length === 0 || (listHeader !== testHeader && testHeader === emptyHeader)) { return `Item ${index}`; }
  }

  return header;
};

/** Testing only */
export const internalTesting = {
  groupBy,
  inAccordion,
  getFieldId,
  getFieldTitle,
  getAccordionSummary,
  getSectionTitle,
};
